import {
  Component,
  Host,
  h,
  Element,
  Prop,
  EventEmitter,
  Event,
  Listen,
  AttachInternals,
} from '@stencil/core';
import { FileChecksum } from '../../utils/file_checksum';
import classnames from 'classnames';

declare global {
  interface Window {
    csrfToken: () => string;
  }
}

declare interface BlobControllerResponse {
  url: string;
  head_url?: string;
  headers?: object;
  key: string;
  file?: File;
}

export interface AhaUploader extends HTMLAhaUploaderElement {}

/**
 * A component for uploading files. The `<aha-uploader>` component is a wrapper around the native `<input type="file">` element. It provides a styled button that can be clicked to open the file picker dialog.
 */
@Component({
  tag: 'aha-uploader',
  styleUrl: 'aha-uploader.css',
  shadow: true,
  formAssociated: true,
})
export class AhaUploader {
  @Element() host!: HTMLAhaUploaderElement;

  /** Optional. The name of the backing `<input type="file">` that will be sent to the server as part of a regular `<form>` submit. */
  @Prop({ reflect: true }) name?: string;

  /** Optional. The accepted file types. */
  @Prop({ reflect: true }) accept?: string;

  /** Optional. Whether to allow multiple files to be uploaded at once. */
  @Prop({ reflect: true }) multiple?: boolean = false;

  /** Optional. URL to upload files directly. If not provided, files will update form fields. */
  @Prop() uploadUrl?: string;

  /** Optional. Whether the uploader is inside an aha-menu component. */
  @Prop() inMenu?: boolean;

  /** Optional. The class to apply to the uploader. */
  @Prop() class?: string = '';

  /** Optional. If true, uploads will be done directly to S3, and then sent to the defined URL with a `direct_uploads` payload */
  @Prop() direct?: boolean = false;

  /** Optional. If true when direct=true and uploadURL is provided, files will be prepared for S3 upload but upload won't begin immediately. Use triggerUpload() to start. */
  @Prop() delayedUpload?: boolean = false;

  // Optional. If true, when a file is uploaded the dropzone will remain present, and will not show the list of uploaded files.
  @Prop() hideUploadedFiles?: boolean = false;

  @AttachInternals() internals?: ElementInternals;

  private inputField!: HTMLInputElement;
  private filesList!: HTMLDivElement;
  private dropzone!: HTMLDivElement;
  private originalDropzoneContent!: string;

  isFileTypeAccepted(file?: File, fileType?: string): boolean {
    if (!this.accept) return true;

    const acceptedTypes = this.accept.split(',').map(type => type.trim().toLowerCase());
    let fileExtension = null;

    if (file) {
      fileType = file.type.toLowerCase();
      fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    }

    return acceptedTypes.some(type => {
      // Handle mime types with wildcards (e.g., "image/*")
      if (type.includes('/*')) {
        const baseType = type.split('/')[0];
        return fileType?.startsWith(`${baseType}/`);
      }
      // Handle specific mime types
      if (type.includes('/')) {
        return fileType === type;
      }
      // Handle file extensions (e.g., ".pdf", ".jpg")
      return type === fileExtension;
    });
  }

  setElementInternalsValue() {
    if (this.multiple && this.inputField.files && this.inputField.files.length > 0) {
      const formData = new FormData();
      Array.from(this.inputField.files).forEach((file, index) => {
        const fieldName = this.name ? `${this.name}[${index}]` : `file${index}`;
        formData.append(fieldName, file);
      });
      this.internals?.setFormValue(formData);
    } else {
      const file = this.inputField.files?.item(0);
      this.internals?.setFormValue(file || null);
    }
  }

  /**
   * Manually trigger upload of selected files. For use when delayedUpload is true.
   */
  async triggerUpload() {
    const files = this.inputField.files;
    if (files && files.length > 0 && this.uploadUrl) {
      await this.uploadFiles(files);
    }
  }

  /**
   * Triggers when a file is selected. The event will contain the selected file(s) in the `detail` property.
   */
  @Event({
    eventName: 'aha-uploader:upload-change',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  changeEvent!: EventEmitter;

  /**
   * Emitted when files are successfully uploaded to the URL
   */
  @Event({
    eventName: 'aha-uploader:upload-success',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  uploadSuccessEvent!: EventEmitter<unknown>;

  /**
   * Emitted when file upload fails
   */
  @Event({
    eventName: 'aha-uploader:upload-error',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  uploadErrorEvent!: EventEmitter<Error>;

  /**
   * Emitted when the file upload is in progress
   */
  @Event({
    eventName: 'aha-uploader:upload-progress',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  uploadProgressEvent!: EventEmitter<number>;

  /**
   * Emitted when file upload starts
   */
  @Event({
    eventName: 'aha-uploader:upload-start',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  uploadStartEvent!: EventEmitter<void>;

  private async uploadDirectFiles(files: FileList) {
    if (!this.uploadUrl) return;

    const blobUploadPromises = Array.from(files).map(async file => {
      const checksum = await FileChecksum.checkPromise(file);
      return fetch('/api/v1/blobs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': window.csrfToken(),
        },
        body: JSON.stringify({
          blob: {
            filename: file.name,
            content_type: file.type,
            byte_size: file.size,
            checksum: checksum,
          },
        }),
      })
        .then(response => {
          return response.json();
        })
        .then((data: BlobControllerResponse) => {
          return new Promise<BlobControllerResponse>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener('progress', e => {
              if (e.loaded && e.total) {
                const pct = e.loaded / e.total;
                this.uploadProgressEvent.emit(pct * 100);
              }
            });
            xhr.upload.addEventListener('error', e => {
              reject(new Error('Upload failed: ' + e.toString()));
            });
            xhr.upload.addEventListener('abort', e => {
              reject(new Error('Upload aborted: ' + e.toString()));
            });
            xhr.addEventListener('load', e => {
              if (xhr.status === 200) {
                resolve({
                  url: data.url,
                  key: data.key,
                  file: file,
                });
              } else {
                reject(new Error('Upload failed ' + xhr.status));
              }
            });
            xhr.open('PUT', data.url, true);
            xhr.setRequestHeader('Content-Type', file.type);
            if (data.headers) {
              for (const [key, value] of Object.entries(data.headers)) {
                xhr.setRequestHeader(key, value);
              }
            }
            try {
              xhr.send(file);
            } catch (e) {
              reject(new Error('Upload failed'));
            }
          }).then(this.waitForUploadProcessing(data));
        });
    });

    Promise.all(blobUploadPromises)
      .then(
        blobUploads =>
          fetch(this.uploadUrl!, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': window.csrfToken(),
            },
            body: JSON.stringify({
              direct_uploads: blobUploads.map(blobUpload => {
                return {
                  blob_id: blobUpload.key,
                };
              }),
            }),
          }),
        error => {
          throw new Error('Failed to upload all files.');
        },
      )
      .then(response => {
        if (response.ok) {
          return response?.json();
        } else {
          throw new Error('Failed to complete the upload.');
        }
      })
      .then(responseData => {
        this.uploadSuccessEvent.emit(responseData);
        this.restoreDropzoneContent();
      })
      .catch(error => {
        this.uploadErrorEvent.emit(error as Error);
        this.restoreDropzoneContent();
      });
  }

  private waitForUploadProcessing(
    data: BlobControllerResponse,
  ):
    | ((value: BlobControllerResponse) => { key: string } | PromiseLike<{ key: string }>)
    | null
    | undefined {
    return async s3UploadResponse => {
      let done = false;
      let iterations = 0;
      const maxRetries = 10;

      while (!done && iterations < maxRetries) {
        try {
          const response = await fetch(`/api/v1/blobs/${encodeURIComponent(data.key)}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': window.csrfToken(),
            },
          });

          if (![200, 202].includes(response.status)) {
            throw new Error('Error processing upload');
          }

          const result = await response.json();

          if (result.status === 'success') {
            done = true;
          } else if (result.status === 'error') {
            throw new Error('File upload failed');
          } else {
            // Still pending
            iterations++;
            await new Promise(r => setTimeout(r, 1000));
          }
        } catch (error) {
          throw error;
        }
      }

      if (!done) {
        throw new Error('File upload timed out');
      }

      return {
        key: data.key,
      };
    };
  }

  private async uploadFiles(files: FileList) {
    if (!this.uploadUrl) return;

    this.uploadStartEvent.emit();
    this.uploadProgressEvent.emit(0);

    if (this.direct) {
      return this.uploadDirectFiles(files);
    }

    const formData = new FormData();
    Array.from(files).forEach((file, index) => {
      const fieldName = this.name ? `${this.name}[${index}]` : `file${index}`;
      formData.append(fieldName, file);
    });

    // Clear the input so that the same file can be uploaded again.
    this.inputField.value = '';

    try {
      // Use XHR for progress tracking but wrap it in a promise to maintain fetch-like API
      const response = await new Promise<Response>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', event => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            this.uploadProgressEvent.emit(percentComplete);
          }
        });

        xhr.onload = () => {
          // This isnt a fetch so we mimic the fetch response
          // and resolve the promise with the response
          // Required as fetch does not support progress events
          if (xhr.status >= 200 && xhr.status < 300) {
            const responseHeaders = new Headers();
            xhr
              .getAllResponseHeaders()
              .trim()
              .split(/[\r\n]+/)
              .forEach(line => {
                const parts = line.split(': ');
                const header = parts.shift();
                const value = parts.join(': ');
                if (header) responseHeaders.append(header, value);
              });

            const response = new Response(xhr.response, {
              status: xhr.status,
              statusText: xhr.statusText,
              headers: responseHeaders,
            });

            resolve(response);
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('Network error occurred'));
        xhr.open('POST', this.uploadUrl!);
        xhr.setRequestHeader('X-CSRF-Token', window.csrfToken());
        xhr.send(formData);
      });

      const responseData = await response.json();
      this.uploadSuccessEvent.emit(responseData);
      this.restoreDropzoneContent();
    } catch (error) {
      this.uploadErrorEvent.emit(error as Error);
      this.restoreDropzoneContent();
    }
  }

  @Listen('aha-uploader:upload-change')
  private async handleFileChange(e: Event) {
    const files = this.inputField.files;

    if (files && files.length > 0) {
      if (this.uploadUrl && !this.multiple) {
        this.updateDropzoneForUrlUpload(files[0]);
        if (!this.delayedUpload) {
          await this.uploadFiles(files);
        }
      } else {
        if (this.filesList) {
          this.filesList.innerHTML = '';
        }
        Array.from(files).forEach(file => this.addFile(file));
        if (this.uploadUrl && !this.delayedUpload) {
          await this.uploadFiles(files);
        }
        if (!this.uploadUrl || this.delayedUpload) {
          this.setElementInternalsValue();
        }
        this.toggleFileListOrDropzone();
      }
    } else {
      if (this.filesList) {
        this.filesList.innerHTML = '';
      }
      this.toggleFileListOrDropzone();
    }
  }

  private handleFileSelected(e: Event) {
    this.changeEvent.emit(this.inputField.files);
  }

  private addFile(file: File) {
    const imageId = `image-${Math.random().toString(36).substring(7)}`;

    const isImage = file.type.startsWith('image');
    const isVideo = file.type.startsWith('video');

    let tag;
    if (isImage) {
      tag = `<img id=${imageId} src='' alt='' />`;
    } else if (isVideo) {
      tag = `<fa-icon class="fa-regular fa-video fa-lg"></fa-icon>`;
    } else {
      tag = `<fa-icon class="fa-regular fa-file fa-lg"></fa-icon>`;
    }

    const fragment = document.createRange().createContextualFragment(`
      <div class="file-item">
        <div class="file-item-content">
          ${tag}
          <span class="file-name">${file.name}</span>
        </div>
        <div class="file-item-actions">
          <button class="remove-file">
            <fa-icon class="fa-regular fa-trash"></fa-icon>
          </button>
          <aha-tooltip>Remove file</aha-tooltip>
        </div>
      </div>
    `);

    if (isImage) {
      fragment.querySelector(`#${imageId}`)?.setAttribute('src', URL.createObjectURL(file));
    }

    fragment.querySelector('.remove-file')?.addEventListener('click', (e: Event) => {
      e.preventDefault();
      const files = this.inputField.files;
      if (files) {
        const transfer = new DataTransfer();
        Array.from(files).forEach(f => {
          if (f !== file) {
            transfer.items.add(f);
          }
        });
        this.inputField.files = transfer.files;
        this.changeEvent.emit(this.inputField.files);
      }
    });

    this.filesList.appendChild(fragment);
  }

  private updateDropzoneForUrlUpload(file: File) {
    if (!this.dropzone) return;
    const content = this.dropzone.querySelector('#dropzone-content');

    if (content) {
      this.originalDropzoneContent = content.innerHTML;

      content.innerHTML = `
        <div class="file-item">
          <div class="file-item-content">
            ${
              file.type.startsWith('image')
                ? `<img src="${URL.createObjectURL(file)}" alt="" />`
                : file.type.startsWith('video')
                ? `<fa-icon class="fa-regular fa-video fa-lg"></fa-icon>`
                : `<fa-icon class="fa-regular fa-file fa-lg"></fa-icon>`
            }
            <aha-loading-dots></aha-loading-dots>
          </div>
        </div>`;
    }
  }

  private restoreDropzoneContent() {
    if (!this.dropzone) return;
    const content = this.dropzone.querySelector('#dropzone-content');
    if (content && this.originalDropzoneContent) {
      content.innerHTML = this.originalDropzoneContent;
    }
  }

  private toggleFileListOrDropzone() {
    if (!this.filesList || !this.dropzone || this.hideUploadedFiles) return;
    if (this.uploadUrl && !this.delayedUpload) return;

    if (this.filesList.children.length > 0) {
      this.filesList.classList.remove('hidden');
      if (!this.multiple) {
        this.dropzone.classList.add('hidden');
      }
    } else {
      this.filesList.classList.add('hidden');
      if (!this.multiple) {
        this.dropzone.classList.remove('hidden');
      }
    }
  }

  private handleDragOver(e: DragEvent) {
    e.preventDefault();
    const target = e.currentTarget;
    const transferItems = e.dataTransfer?.items;
    if (transferItems && target instanceof HTMLElement) {
      if (Array.from(transferItems).some(item => !this.isFileTypeAccepted(undefined, item.type))) {
        target.classList.add('drag-over-error');
        e.dataTransfer.dropEffect = 'none';
      } else {
        target.classList.add('drag-over');
        e.dataTransfer.dropEffect = 'copy';
      }
    }
  }

  private handleDragLeave(e: DragEvent) {
    e.preventDefault();
    (e.currentTarget as HTMLElement)?.classList?.remove('drag-over', 'drag-over-error');
  }

  handleDrop(e: DragEvent) {
    e.preventDefault();
    const droppedFiles = e.dataTransfer?.files;
    if (droppedFiles && droppedFiles.length > 0) {
      const transfer = new DataTransfer();

      if (this.multiple && this.inputField.files) {
        Array.from(this.inputField.files).forEach(file => {
          transfer.items.add(file);
        });
      }

      // Filter files based on accept attribute
      const validFiles = Array.from(droppedFiles).filter(file => this.isFileTypeAccepted(file));

      if (validFiles.length > 0) {
        if (!this.multiple) {
          transfer.items.add(validFiles[0]);
        } else {
          validFiles.forEach(file => {
            transfer.items.add(file);
          });
        }

        this.inputField.files = transfer.files;
        this.changeEvent.emit(this.inputField.files);
      }
    }
    (e.currentTarget as HTMLElement)?.classList?.remove('drag-over', 'drag-over-error');
  }

  connectedCallback() {
    this.handleFileChange = this.handleFileChange.bind(this.host);
    this.handleFileSelected = this.handleFileSelected.bind(this.host);
    this.handleDragOver = this.handleDragOver.bind(this.host);
    this.handleDrop = this.handleDrop.bind(this.host);
    this.handleDragLeave = this.handleDragLeave.bind(this.host);
  }

  componentDidLoad() {
    this.setElementInternalsValue();
  }

  formAssociatedCallback(form: HTMLFormElement | null) {
    if (form) {
      form.enctype = 'multipart/form-data';
    }
  }

  render() {
    return (
      <Host>
        <div
          class={classnames('aha-uploader', {
            ['aha-uploader--in-menu']: this.inMenu,
          })}
        >
          <input
            type="file"
            name={this.name}
            ref={ref => (this.inputField = ref as HTMLInputElement)}
            accept={this.accept}
            multiple={this.multiple}
            onChange={this.handleFileSelected}
            style={{ display: 'none' }}
            slot="form-control"
          />
          {this.inMenu && (
            <button
              onClick={e => {
                e.preventDefault();
                this.inputField.click();
              }}
            >
              <slot name="dropzone-text"></slot>
            </button>
          )}
          {!this.inMenu && (
            <div class={`upload-container ${this.class || ''}`}>
              <div
                id="dropzone"
                part="dropzone"
                ref={ref => (this.dropzone = ref as HTMLDivElement)}
                onDragOver={this.handleDragOver}
                onDragLeave={this.handleDragLeave}
                onDrop={this.handleDrop}
              >
                <div id="dropzone-content">
                  <slot name="dropzone-icon">
                    <fa-icon class="fa-regular fa-upload"></fa-icon>
                  </slot>
                  <slot name="dropzone-text">
                    Drag and drop {this.multiple ? 'files' : 'file'} here or
                  </slot>{' '}
                  <button
                    id="dropzone-button"
                    onClick={e => {
                      e.preventDefault();
                      this.inputField.click();
                    }}
                  >
                    <slot name="dropzone-button">choose {this.multiple ? 'files' : 'file'}</slot>
                  </button>
                </div>
              </div>

              <div
                id="file-list"
                class="hidden"
                ref={ref => (this.filesList = ref as HTMLDivElement)}
              ></div>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
